// Scenario path for the Skill Manager Body Map (AXISREF499).
//
// A user has a referral in mind ("knee, ruptured, sports injury") and wants to check which
// urgency skills would apply and the priority they lead to. This does not run the AI: it
// matches the user's clue words against the real skill text, and uses a built-in map of how
// the triage skills relate (signal -> pattern -> precedence). Funding and routing skills are
// reported separately because they never change the priority.

import type { MappedSkill } from '../services/skillMapService';

export interface ClueHit {
  clue: string;
  terms: string[];
}

interface SignalDef {
  key: string;
  skill: string;
  label: string;
}

interface PatternDef {
  key: string;
  skill: string;
  priority: number;
  range?: string;
  signals: string[];
  requires?: string;
  conditional?: { text: string; clue: string; group: string };
  note?: Partial<Record<string, string>>;
}

interface RedFlagDef {
  skill: string;
  clue: string;
}

interface OtherDef {
  group: 'Funding' | 'Where it goes';
  skill: string;
  regions?: string[];
  why: (ctx: { sport: boolean; region: string }) => string;
}

// Signal skills: what the AI looks for in a referral.
const SIGNALS: SignalDef[] = [
  { key: 'onset', skill: 'Onset and Mechanism', label: 'How it happened' },
  { key: 'findings', skill: 'Objective or Described Findings', label: 'What was found' },
  { key: 'function', skill: 'Functional Limitation', label: "What they can't do" },
  { key: 'time', skill: 'Time Course', label: 'How long ago' },
  { key: 'age', skill: 'Age and Population', label: 'Who the patient is' },
];

// Pattern skills: themes that point to a priority, and the signals that feed them.
const PATTERNS: PatternDef[] = [
  { key: 'rupture', skill: 'Tendon Rupture', priority: 1, signals: ['onset', 'findings', 'function'] },
  {
    key: 'acute-joint',
    skill: 'Acute Joint Injury',
    priority: 1,
    signals: ['onset', 'findings', 'function', 'time'],
    note: { knee: 'An acute knee injury always triages to P1.' },
  },
  {
    key: 'avulsion',
    skill: 'Avulsion Injury',
    priority: 1,
    signals: ['onset', 'age'],
    conditional: { text: 'Only if the patient is under 20', clue: 'under 20', group: 'young' },
  },
  { key: 'stress', skill: 'Stress Fracture', priority: 1, range: 'P1–2', signals: ['findings', 'function'] },
  { key: 'chronic', skill: 'Chronic / Degenerative', priority: 3, range: 'P2–3', signals: ['onset', 'time', 'age'], requires: 'chronic' },
  {
    key: 'representative',
    skill: 'Representative athlete',
    priority: 1,
    signals: ['age'],
    conditional: { text: 'Only if the patient is a national or representative athlete', clue: 'national rep', group: 'rep' },
  },
];

// Red flags with a plain-words clue that would make them fire. Any red flag escalates to P1.
const RED_FLAGS: RedFlagDef[] = [
  { skill: 'Inability to Extend Knee', clue: "can't straighten" },
  { skill: 'Mechanical Locking', clue: 'locked knee' },
  { skill: 'Haemarthrosis', clue: 'blood in joint' },
  { skill: 'Large Effusion', clue: 'swollen' },
  { skill: 'Inability to Weight Bear', clue: "can't weight bear" },
  { skill: 'Compartment Syndrome', clue: 'compartment syndrome' },
  { skill: 'Saddle Numbness', clue: 'saddle numbness' },
];

const PRECEDENCE_SKILL = 'Default triage extraction';

// Everyday words a user types, mapped to the words the skills use.
const CLUE_WORDS: { inputs: string[]; words: string[]; group?: string }[] = [
  { inputs: ['rupture', 'ruptured', 'torn', 'tear', 'snapped'], words: ['rupture', 'ruptures', 'snap', 'popping', 'palpable gap'] },
  { inputs: ['sport', 'sports', 'sports injury', 'rugby', 'netball', 'football', 'tackle', 'training'], words: ['rugby tackle', 'kicking or sprinting', 'twisting', 'pivoting'], group: 'sport' },
  { inputs: ['pop', 'popped', 'felt a pop'], words: ['pop', 'popping', 'felt a pop', 'snap'] },
  { inputs: ['weight bear', "can't weight bear", 'unable to walk'], words: ['weight bear', 'bear weight', 'difficulty walking'] },
  { inputs: ['locked', 'locking', 'locked knee', 'catching'], words: ['locked', 'locking', 'locked knee', 'catching'] },
  { inputs: ['swollen', 'swelling', 'effusion'], words: ['effusion', 'swollen', 'swelling'] },
  { inputs: ["can't straighten", 'cannot straighten', 'unable to extend'], words: ['extend knee', 'fully extend'] },
  { inputs: ['blood in joint', 'haemarthrosis'], words: ['blood in joint', 'haemarthrosis'] },
  { inputs: ['national rep', 'representative', 'elite', 'professional'], words: ['representative athlete', 'national representative', 'professional sport', 'competitive athlete'], group: 'rep' },
  { inputs: ['over 12 months', 'chronic', 'long-standing', 'gradual', 'overuse'], words: ['>12 months', 'chronic', 'gradual onset', 'insidious onset', 'over months'], group: 'chronic' },
  { inputs: ['acute', 'recent', 'this week', 'last week'], words: ['acute', 'within 4 weeks'] },
  { inputs: ['teenager', 'under 20', 'young'], words: ['under 20'], group: 'young' },
];

// Skills that apply to the scenario but never change its priority.
const OTHER: OtherDef[] = [
  { group: 'Funding', skill: 'ACC-related determination', why: c => (c.sport ? '"Sports injury" makes it ACC related' : 'Checks for an accident, sport or work injury') },
  { group: 'Funding', skill: 'Funder information', why: c => (c.sport ? 'ACC related, so the funder is ACC' : 'Works out who pays') },
  { group: 'Where it goes', skill: 'Referral type determination', why: c => (c.region === 'knee' ? 'Knee goes to Acute Knee Clinic' : 'Body part and condition set the referral type') },
  { group: 'Where it goes', skill: 'AKC injuries need to be less than 4 weeks old', regions: ['knee', 'shoulder'], why: () => 'Acute Knee Clinic only within 4 weeks and with an ACC number' },
  { group: 'Where it goes', skill: 'Default clinic selection', why: () => 'Nearest active clinic to the patient' },
];

export const CLUE_SUGGESTIONS = ['ruptured', 'sports injury', 'felt a pop', "can't weight bear", 'locked knee', 'national rep', 'under 20', 'over 12 months'];

const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const wholeWord = (w: string) => new RegExp(`(^|[^a-z])${escapeRegex(w.toLowerCase())}(?=[^a-z]|$)`, 'i');

function clueGroups(clue: string) {
  const c = clue.toLowerCase().trim();
  return CLUE_WORDS.filter(g => g.inputs.some(i => c.includes(i) || i.includes(c)));
}

export function termsFor(clue: string): string[] {
  const c = clue.toLowerCase().trim();
  const terms = new Set([c]);
  clueGroups(c).forEach(g => g.words.forEach(w => terms.add(w)));
  return [...terms];
}

export function hitsFor(text: string, clues: string[]): ClueHit[] {
  return clues
    .map(clue => ({ clue, terms: termsFor(clue).filter(t => wholeWord(t).test(text)) }))
    .filter(h => h.terms.length > 0);
}

export const uniqueTerms = (hits: ClueHit[]) => [...new Set(hits.flatMap(h => h.terms))];

/** Skill names on staging carry long suffixes, e.g. "Tendon Rupture (usually Triage 1)". */
export const shortName = (name: string) => name.replace(/\s*\(.*\)$/, '').replace(/\s*—.*$/, '');

/**
 * A compact, human name for a skill in tight UI (trace nodes, breadcrumbs, chips). Long rule
 * names that read as sentences are cut to their first sentence; the precedence rule gets its role.
 */
export function skillDisplayName(skill: Pick<MappedSkill, 'name'>): string {
  if (skill.name.startsWith(PRECEDENCE_SKILL)) return 'Priority precedence';
  const name = shortName(skill.name).split(/\.\s/)[0].replace(/\.$/, '').trim();
  return name || skill.name;
}

export interface PatternMatch {
  key: string;
  skill: MappedSkill;
  priority: number;
  range?: string;
  hits: ClueHit[];
  note?: string;
}

export interface SignalMatch {
  key: string;
  label: string;
  skill: MappedSkill;
  hits: ClueHit[];
}

export interface FlagMatch {
  skill: MappedSkill;
  hits: ClueHit[];
}

export interface ScenarioCheck {
  name: string;
  why: string;
  clue: string;
}

export interface ScenarioResult {
  followed: PatternMatch | null;
  alternatives: PatternMatch[];
  signals: SignalMatch[];
  missingSignals: string[];
  firedFlags: FlagMatch[];
  priority: number | null;
  precedence: MappedSkill | null;
  checks: ScenarioCheck[];
  other: { group: string; skill: MappedSkill; why: string }[];
}

export function computeScenario(
  skills: MappedSkill[],
  region: string,
  clues: string[],
  follow: string | null = null,
): ScenarioResult {
  const find = (prefix: string) => skills.find(s => s.name.startsWith(prefix));
  const appliesHere = (s: MappedSkill) => !s.regions.length || s.regions.some(r => r.code === region);
  const textOf = (s: MappedSkill) => `${s.name} ${s.description ?? ''}`;
  const groups = new Set(clues.flatMap(clueGroups).map(g => g.group).filter(Boolean) as string[]);

  const patterns = PATTERNS.flatMap(def => {
    const skill = find(def.skill);
    if (!skill || !appliesHere(skill)) return [];
    const hits = hitsFor(textOf(skill), clues);
    const gated = !!def.requires && !groups.has(def.requires);
    const conditionMet = !def.conditional || groups.has(def.conditional.group);
    return [{ def, skill, hits, matched: hits.length > 0 && !gated && conditionMet }];
  });

  const matched: PatternMatch[] = patterns
    .filter(p => p.matched)
    .map(p => ({
      key: p.def.key,
      skill: p.skill,
      priority: p.def.priority,
      range: p.def.range,
      hits: p.hits,
      note: p.def.note?.[region],
    }))
    .sort((a, b) => a.priority - b.priority);

  const followed = matched.find(p => p.key === follow) ?? matched[0] ?? null;
  const followedDef = followed ? PATTERNS.find(p => p.key === followed.key) : undefined;

  const signals: SignalMatch[] = [];
  const missingSignals: string[] = [];
  for (const key of followedDef?.signals ?? []) {
    const def = SIGNALS.find(s => s.key === key);
    const skill = def && find(def.skill);
    if (!def || !skill) continue;
    const hits = hitsFor(skill.description ?? '', clues);
    if (hits.length) signals.push({ key, label: def.label, skill, hits });
    else missingSignals.push(def.label.toLowerCase());
  }

  const flags = RED_FLAGS.flatMap(def => {
    const skill = find(def.skill);
    return skill && appliesHere(skill) ? [{ def, skill, hits: hitsFor(textOf(skill), clues) }] : [];
  });
  const firedFlags = flags.filter(f => f.hits.length).map(f => ({ skill: f.skill, hits: f.hits }));

  const checks: ScenarioCheck[] = [
    ...patterns
      .filter(p => p.def.conditional && !p.matched)
      .map(p => ({ name: shortName(p.skill.name), why: p.def.conditional!.text, clue: p.def.conditional!.clue })),
    ...flags
      .filter(f => !f.hits.length && f.skill.regions.some(r => r.code === region))
      .map(f => ({ name: f.skill.name, why: 'Red flag written for this body part', clue: f.def.clue })),
    ...flags
      .filter(f => !f.hits.length && !f.skill.regions.length)
      .slice(0, 3)
      .map(f => ({ name: f.skill.name, why: 'Red flag that applies to every referral', clue: f.def.clue })),
  ].filter(c => !clues.includes(c.clue));

  const ctx = { sport: groups.has('sport'), region };
  const other = OTHER.flatMap(def => {
    const skill = find(def.skill);
    if (!skill || (def.regions && !def.regions.includes(region))) return [];
    return [{ group: def.group, skill, why: def.why(ctx) }];
  });

  let priority = followed ? followed.priority : null;
  if (firedFlags.length) priority = 1;

  return {
    followed,
    alternatives: matched.filter(p => p !== followed),
    signals,
    missingSignals,
    firedFlags,
    priority,
    precedence: find(PRECEDENCE_SKILL) ?? null,
    checks,
    other,
  };
}

// ---------------------------------------------------------------------------
// Skill trace (AXISREF499): from one urgency skill, the related skills on either side of it
// and the priority it can lead to. Uses the same built-in map as the scenario check.

export type TraceRole = 'signal' | 'pattern' | 'flag' | 'precedence' | 'rule';

export interface TraceNode {
  id: string;
  skill: MappedSkill | null;
  label: string;
  note?: string;
  priority?: number;
}

export interface SkillTrace {
  role: TraceRole;
  columns: { title: string; nodes: TraceNode[] }[];
  links: [string, string][];
  outcome: string | null;
  alsoApplied: { group: string; skill: MappedSkill }[];
}

const ROLE_LABEL: Record<TraceRole, string> = {
  signal: 'Signal the AI looks for',
  pattern: 'Pattern',
  flag: 'Red flag',
  precedence: 'Priority precedence',
  rule: 'Triage rule',
};

export const traceRoleLabel = (role: TraceRole) => ROLE_LABEL[role];

const priorityLabel = (p: { priority: number; range?: string }) => p.range ?? `P${p.priority}`;

export function roleOf(skill: MappedSkill): TraceRole {
  if (skill.kind === 'red_flag') return 'flag';
  if (skill.name.startsWith(PRECEDENCE_SKILL)) return 'precedence';
  if (PATTERNS.some(p => skill.name.startsWith(p.skill))) return 'pattern';
  if (SIGNALS.some(s => skill.name.startsWith(s.skill))) return 'signal';
  return 'rule';
}

/** The priority a skill can lead to, for the small pill on each urgency row. */
export function outcomeOf(skills: MappedSkill[], region: string, skill: MappedSkill): string | null {
  return traceSkill(skills, region, skill).outcome;
}

export function traceSkill(skills: MappedSkill[], region: string, skill: MappedSkill): SkillTrace {
  const find = (prefix: string) => skills.find(s => s.name.startsWith(prefix));
  const appliesHere = (s: MappedSkill) => !s.regions.length || s.regions.some(r => r.code === region);
  const role = roleOf(skill);
  const precedence = find(PRECEDENCE_SKILL) ?? null;
  const node = (s: MappedSkill, extra: Partial<TraceNode> = {}): TraceNode => ({
    id: `skill:${s.kind}:${s.id}`,
    skill: s,
    label: skillDisplayName(s),
    ...extra,
  });
  const priorityNode = (label: string, priority: number): TraceNode => ({
    id: `priority:${label}`,
    skill: null,
    label,
    priority,
  });
  const precedenceNode = precedence ? node(precedence, { label: 'Priority precedence' }) : null;

  const patternsHere = PATTERNS.flatMap(def => {
    const s = find(def.skill);
    return s && appliesHere(s) ? [{ def, skill: s }] : [];
  });
  const patternNote = (def: PatternDef) =>
    def.conditional ? def.conditional.text : def.requires === 'chronic' ? 'Only for chronic presentations' : def.note?.[region];

  const columns: SkillTrace['columns'] = [];
  const links: [string, string][] = [];
  let outcome: string | null = null;

  // Patterns -> precedence -> priority, shared by every role that reaches a pattern.
  const toPriority = (patterns: { def: PatternDef; skill: MappedSkill }[]) => {
    const priorities = new Map<string, TraceNode>();
    patterns.forEach(p => {
      const label = priorityLabel(p.def);
      if (!priorities.has(label)) priorities.set(label, priorityNode(label, p.def.priority));
    });
    const ordered = [...priorities.values()].sort((a, b) => (a.priority ?? 9) - (b.priority ?? 9));
    if (precedenceNode) {
      columns.push({ title: 'Decided by', nodes: [precedenceNode] });
      patterns.forEach(p => links.push([node(p.skill).id, precedenceNode.id]));
      ordered.forEach(pr => links.push([precedenceNode.id, pr.id]));
    } else {
      patterns.forEach(p => links.push([node(p.skill).id, `priority:${priorityLabel(p.def)}`]));
    }
    columns.push({ title: 'Priority', nodes: ordered });
    outcome = ordered.length ? ordered.map(o => o.label).join(' / ') : null;
  };

  if (role === 'pattern') {
    const def = PATTERNS.find(p => skill.name.startsWith(p.skill))!;
    const feeds = def.signals.flatMap(key => {
      const sig = SIGNALS.find(s => s.key === key);
      const s = sig && find(sig.skill);
      return s ? [node(s, { note: sig!.label })] : [];
    });
    if (feeds.length) columns.push({ title: 'Looks for', nodes: feeds });
    const self = node(skill, { note: patternNote(def) });
    columns.push({ title: 'Pattern', nodes: [self] });
    feeds.forEach(f => links.push([f.id, self.id]));
    toPriority([{ def, skill }]);
  } else if (role === 'signal') {
    const sig = SIGNALS.find(s => skill.name.startsWith(s.skill))!;
    const self = node(skill, { note: sig.label });
    columns.push({ title: 'Looks for', nodes: [self] });
    const fed = patternsHere.filter(p => p.def.signals.includes(sig.key));
    columns.push({ title: 'Feeds these patterns', nodes: fed.map(p => node(p.skill, { note: patternNote(p.def) })) });
    fed.forEach(p => links.push([self.id, node(p.skill).id]));
    toPriority(fed);
  } else if (role === 'precedence') {
    columns.push({ title: 'Patterns', nodes: patternsHere.map(p => node(p.skill, { note: patternNote(p.def) })) });
    toPriority(patternsHere);
    const self = columns.find(c => c.title === 'Decided by');
    if (self) self.nodes = [node(skill, { label: 'Priority precedence', note: 'The most urgent match wins' })];
  } else if (role === 'flag') {
    const self = node(skill, { note: 'Any red flag escalates to P1' });
    columns.push({ title: 'Red flag', nodes: [self] });
    const p1 = priorityNode('P1', 1);
    if (precedenceNode) {
      columns.push({ title: 'Decided by', nodes: [precedenceNode] });
      links.push([self.id, precedenceNode.id], [precedenceNode.id, p1.id]);
    } else {
      links.push([self.id, p1.id]);
    }
    columns.push({ title: 'Priority', nodes: [p1] });
    outcome = 'P1';
  } else {
    const self = node(skill, { note: 'Weighed alongside the other triage rules' });
    columns.push({ title: 'Triage rule', nodes: [self] });
    if (precedenceNode && precedence!.id !== skill.id) {
      columns.push({ title: 'Decided by', nodes: [precedenceNode] });
      links.push([self.id, precedenceNode.id]);
    }
  }

  const alsoApplied = OTHER.flatMap(def => {
    const s = find(def.skill);
    return s && (!def.regions || def.regions.includes(region)) ? [{ group: def.group, skill: s }] : [];
  });

  return { role, columns: columns.filter(c => c.nodes.length), links, outcome, alsoApplied };
}
